/**
 * Phase 131B — Shared StreamX Chat Panel
 * ------------------------------------
 * Used by both studio (broadcaster) and watch (viewer) so the chat surface
 * stays in lock-step. Features:
 *   - Pinned message banner (persisted server-side, propagated via data channel)
 *   - Emoji reactions (floating overlay)
 *   - Slow mode countdown for viewers
 *   - Broadcaster: pin/unpin button, slow mode selector, soft mute control
 *   - Message tiles with strong contrast (Phase 131A doctrine)
 *
 * Props:
 *   stream          — stream doc from backend (has pinned_message, slow_mode_seconds)
 *   isBroadcaster   — true on studio side
 *   viewerName      — displayed name in outgoing messages
 *   onStreamUpdate  — callback when broadcaster persists pin/slow-mode changes
 *   apiClient       — axios-like instance (api on studio side, axios on watch side)
 *   apiPathBase     — '/api/livex/streams/{id}' prefix builder
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import {
  MessageCircle, Send, Pin, PinOff, Timer, X, EyeOff, Smile,
} from 'lucide-react';

const REACTION_EMOJIS = ['🔥', '⚽', '🏏', '🏆', '👏', '💪', '😂', '🎯'];
const SLOW_MODE_OPTIONS = [
  { seconds: 0, label: 'Off' },
  { seconds: 5, label: '5s' },
  { seconds: 10, label: '10s' },
  { seconds: 30, label: '30s' },
];

export default function LivexChatPanel({
  stream,
  isBroadcaster = false,
  viewerName,
  onStreamUpdate,
  apiClient,
  apiPathBase,
  showPreviewBadge = false,
}) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const listRef = useRef(null);
  const floatingRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [pinned, setPinned] = useState(stream?.pinned_message || '');
  const [slowMode, setSlowMode] = useState(stream?.slow_mode_seconds || 0);
  const [mutedIdentities, setMutedIdentities] = useState(() => new Set());
  const [floatingReactions, setFloatingReactions] = useState([]); // {id, emoji}
  const [showEmojiBar, setShowEmojiBar] = useState(false);
  const [showSlowMenu, setShowSlowMenu] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [highlightBanner, setHighlightBanner] = useState(null); // {emoji,label,match_time_label}
  const lastSentAt = useRef(0);
  const highlightTimerRef = useRef(null);

  // Listen for data channel packets (chat, pin, slow_mode, reaction, mute).
  useEffect(() => {
    if (!room) return;
    const onData = (payload, participant, _kind, topic) => {
      try {
        const txt = new TextDecoder().decode(payload);
        const obj = JSON.parse(txt);
        const fromId = participant?.identity || 'system';
        if (topic === 'livex-chat' && obj?.text) {
          if (mutedIdentities.has(fromId)) return;
          setMessages((prev) => [...prev.slice(-49), {
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            name: obj.name || fromId,
            identity: fromId,
            text: String(obj.text).slice(0, 240),
            isHost: !!obj.isHost,
          }]);
        } else if (topic === 'livex-pin' && typeof obj?.text === 'string') {
          setPinned(obj.text);
        } else if (topic === 'livex-slow' && typeof obj?.seconds === 'number') {
          setSlowMode(obj.seconds);
        } else if (topic === 'livex-react' && obj?.emoji) {
          spawnFloating(obj.emoji);
        } else if (topic === 'livex-mute' && obj?.identity) {
          // Everyone hides the muted identity from their UI.
          setMutedIdentities((s) => new Set(s).add(obj.identity));
        } else if (topic === 'livex-highlight' && obj?.emoji) {
          // Flash banner: a highlight was just marked by the broadcaster.
          if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
          setHighlightBanner({
            emoji: obj.emoji,
            label: obj.label || 'Highlight',
            match_time_label: obj.match_time_label || '',
          });
          highlightTimerRef.current = setTimeout(() => setHighlightBanner(null), 5000);
        }
      } catch { /* ignore malformed */ }
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => { room.off(RoomEvent.DataReceived, onData); };
  }, [room, mutedIdentities]);

  // Re-sync from server on stream prop change (initial load + history reloads).
  useEffect(() => {
    setPinned(stream?.pinned_message || '');
    setSlowMode(stream?.slow_mode_seconds || 0);
  }, [stream?.pinned_message, stream?.slow_mode_seconds]);

  // Auto-scroll chat list.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  // Slow-mode cooldown countdown.
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const id = setInterval(() => setCooldownLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldownLeft]);

  // Floating reactions cleanup.
  const spawnFloating = useCallback((emoji) => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setFloatingReactions((prev) => [...prev.slice(-9), { id, emoji }]);
    setTimeout(() => {
      setFloatingReactions((prev) => prev.filter((r) => r.id !== id));
    }, 2200);
  }, []);

  const publish = useCallback(async (topic, obj) => {
    if (!localParticipant) return false;
    try {
      const buf = new TextEncoder().encode(JSON.stringify(obj));
      await localParticipant.publishData(buf, { reliable: true, topic });
      return true;
    } catch {
      return false;
    }
  }, [localParticipant]);

  const sendChat = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text) return;

    // Slow-mode enforcement (viewers only; broadcaster bypasses).
    if (!isBroadcaster && slowMode > 0) {
      const elapsed = (Date.now() - lastSentAt.current) / 1000;
      if (elapsed < slowMode) {
        setCooldownLeft(Math.ceil(slowMode - elapsed));
        return;
      }
    }

    const ok = await publish('livex-chat', { name: viewerName, text, isHost: isBroadcaster });
    if (!ok) return;
    lastSentAt.current = Date.now();
    if (!isBroadcaster && slowMode > 0) setCooldownLeft(slowMode);

    // Optimistic local echo.
    setMessages((prev) => [...prev.slice(-49), {
      id: `${Date.now()}_local`,
      name: viewerName,
      identity: localParticipant?.identity || 'me',
      text,
      mine: true,
      isHost: isBroadcaster,
    }]);
    setDraft('');
  };

  const sendReaction = (emoji) => {
    publish('livex-react', { emoji });
    spawnFloating(emoji);
    setShowEmojiBar(false);
  };

  // Broadcaster-only actions.
  const broadcasterPin = async (text) => {
    if (!apiClient || !apiPathBase) return;
    try {
      const { data } = await apiClient.post(`${apiPathBase}/pin`, { text });
      setPinned(data.pinned_message || '');
      publish('livex-pin', { text: data.pinned_message || '' });
      onStreamUpdate?.({ pinned_message: data.pinned_message || '' });
    } catch { /* ignore */ }
  };

  const broadcasterSlow = async (seconds) => {
    if (!apiClient || !apiPathBase) return;
    try {
      const { data } = await apiClient.post(`${apiPathBase}/slow-mode`, { seconds });
      setSlowMode(data.slow_mode_seconds);
      publish('livex-slow', { seconds: data.slow_mode_seconds });
      onStreamUpdate?.({ slow_mode_seconds: data.slow_mode_seconds });
      setShowSlowMenu(false);
    } catch { /* ignore */ }
  };

  const broadcasterMute = (msg) => {
    if (!msg.identity || msg.identity === localParticipant?.identity) return;
    setMutedIdentities((s) => new Set(s).add(msg.identity));
    publish('livex-mute', { identity: msg.identity });
  };

  const placeholder = useMemo(() => {
    if (cooldownLeft > 0) return `Slow mode — wait ${cooldownLeft}s`;
    if (isBroadcaster) return 'Talk to your viewers...';
    return 'Cheer the team on...';
  }, [cooldownLeft, isBroadcaster]);

  const inputDisabled = !isBroadcaster && cooldownLeft > 0;

  return (
    <div className="flex flex-col h-full bg-[#07111F] border-2 border-[#22314F] rounded-xl overflow-hidden relative" data-testid="livex-watch-chat">
      {/* Header */}
      <div className="px-4 py-3 border-b-2 border-[#22314F] bg-[#0c1a2e] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <MessageCircle className="h-4 w-4 text-[#FF5A1F]" />
          <div className="livex-label" style={{ marginBottom: 0 }}>Live Chat</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {slowMode > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40" data-testid="livex-chat-slow-badge">
              <Timer className="h-3 w-3" /> {slowMode}s
            </span>
          )}
          {showPreviewBadge && (
            <span className="text-[10px] font-black uppercase tracking-[0.08em] px-2 py-0.5 bg-amber-500 text-black rounded">Preview</span>
          )}
          {isBroadcaster && (
            <div className="relative">
              <button
                onClick={() => setShowSlowMenu((v) => !v)}
                data-testid="livex-chat-slowmode-toggle"
                className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded bg-[#07111F] border border-[#22314F] text-white hover:border-[#FF5A1F] transition-colors"
              >
                <Timer className="h-3 w-3" /> Slow
              </button>
              {showSlowMenu && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-[#0c1a2e] border-2 border-[#22314F] rounded-lg p-1.5 shadow-lg min-w-[110px]">
                  {SLOW_MODE_OPTIONS.map((opt) => (
                    <button
                      key={opt.seconds}
                      onClick={() => broadcasterSlow(opt.seconds)}
                      data-testid={`livex-chat-slowmode-${opt.seconds}`}
                      className={`w-full text-left px-3 py-1.5 text-xs font-bold rounded transition-colors ${
                        slowMode === opt.seconds
                          ? 'bg-[#FF5A1F] text-white'
                          : 'text-white hover:bg-[#22314F]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pinned banner */}
      {pinned && (
        <div className="px-3 py-2.5 bg-[#1a0e10] border-b-2 border-[#3a1a1f] flex items-start gap-2" data-testid="livex-chat-pinned">
          <Pin className="h-4 w-4 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-[14px] font-medium text-white leading-snug">{pinned}</div>
          {isBroadcaster && (
            <button
              onClick={() => broadcasterPin('')}
              data-testid="livex-chat-unpin"
              className="flex-shrink-0 text-[#94A3B8] hover:text-white transition-colors"
              title="Unpin"
            >
              <PinOff className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Transient highlight flash banner — visible to all participants for 5s */}
      {highlightBanner && (
        <div
          className="px-3 py-2.5 bg-gradient-to-r from-[#FF5A1F] to-[#dc2626] border-b-2 border-[#FF5A1F] flex items-center gap-2 animate-livex-flash"
          data-testid="livex-chat-highlight-banner"
        >
          <span className="text-2xl leading-none" aria-hidden>{highlightBanner.emoji}</span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.1em] text-white/90">Highlight</div>
            <div className="text-sm font-black text-white truncate">
              {highlightBanner.label}{' '}
              {highlightBanner.match_time_label && (
                <span className="font-mono tabular-nums text-white/90 ml-1">@ {highlightBanner.match_time_label}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating reactions overlay */}
      <div className="absolute bottom-20 right-3 pointer-events-none flex flex-col-reverse items-end gap-1 z-10" ref={floatingRef}>
        {floatingReactions.map((r) => (
          <div
            key={r.id}
            className="text-3xl animate-livex-float"
            style={{ animation: 'livexFloat 2.2s ease-out forwards' }}
          >
            {r.emoji}
          </div>
        ))}
      </div>

      {/* Message list */}
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[260px] max-h-[60vh]">
        {messages.length === 0 ? (
          <div className="text-center py-8 livex-body-text">
            {isBroadcaster
              ? 'Say hi to your viewers. Pin a message so late-joiners catch up.'
              : 'Say something nice — your message will be visible to everyone watching.'}
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex flex-col ${m.mine ? 'items-end' : 'items-start'} group`}>
              <div className="flex items-center gap-1.5 mb-1 px-1">
                <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">{m.name}</span>
                {m.isHost && (
                  <span className="text-[9px] font-black uppercase tracking-[0.08em] px-1.5 py-0.5 rounded bg-[#FF5A1F] text-white">Host</span>
                )}
              </div>
              <div className="flex items-end gap-1.5 max-w-[90%]">
                <div className={`px-4 py-2.5 rounded-lg text-[15px] font-medium border-2 leading-relaxed ${
                  m.mine
                    ? 'bg-[#FF5A1F] text-white border-[#FF5A1F]'
                    : m.isHost
                      ? 'bg-[#3a1a1f] text-white border-[#FF5A1F]'
                      : 'bg-[#0c1a2e] text-white border-[#22314F]'
                }`} style={{ letterSpacing: '0.01em' }}>
                  {m.text}
                </div>
                {isBroadcaster && !m.mine && !m.isHost && (
                  <button
                    onClick={() => broadcasterMute(m)}
                    data-testid={`livex-chat-mute-${m.identity}`}
                    className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-1 rounded text-[#94A3B8] hover:text-rose-300 hover:bg-rose-950/30 transition-all"
                    title="Mute this viewer for everyone"
                  >
                    <EyeOff className="h-3.5 w-3.5" />
                  </button>
                )}
                {isBroadcaster && !m.mine && (
                  <button
                    onClick={() => broadcasterPin(m.text)}
                    data-testid={`livex-chat-pin-${m.identity}`}
                    className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-1 rounded text-[#94A3B8] hover:text-[#FF5A1F] hover:bg-[#1a0e10] transition-all"
                    title="Pin this message"
                  >
                    <Pin className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Emoji bar */}
      {showEmojiBar && (
        <div className="px-3 py-2 border-t-2 border-[#22314F] bg-[#0c1a2e] flex items-center gap-1.5 flex-wrap" data-testid="livex-chat-emoji-bar">
          {REACTION_EMOJIS.map((em) => (
            <button
              key={em}
              onClick={() => sendReaction(em)}
              data-testid={`livex-chat-emoji-${em}`}
              className="text-2xl hover:scale-125 transition-transform p-1"
            >
              {em}
            </button>
          ))}
          <button
            onClick={() => setShowEmojiBar(false)}
            className="ml-auto text-[#94A3B8] hover:text-white p-1"
            aria-label="Close emoji bar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Composer */}
      <form onSubmit={sendChat} className="p-3 border-t-2 border-[#22314F] bg-[#050a14] flex gap-2">
        <button
          type="button"
          onClick={() => setShowEmojiBar((v) => !v)}
          data-testid="livex-chat-emoji-toggle"
          className="flex-shrink-0 px-3 py-2.5 text-white border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg bg-[#07111F] transition-colors"
          title="React with an emoji"
        >
          <Smile className="h-4 w-4" />
        </button>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          maxLength={240}
          disabled={inputDisabled}
          data-testid="livex-watch-chat-input"
          className="livex-input flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
        />
        <button
          type="submit"
          disabled={!draft.trim() || inputDisabled}
          data-testid="livex-watch-chat-send"
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-bold text-white border-2 border-[#FF5A1F] rounded-lg transition-all disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
